import { useLocation } from '@solidjs/router'
import { isAuthenticated } from '@state'
import { Show, createMemo, onMount, createSignal } from 'solid-js'
import { Motion, Presence } from 'solid-motionone'
import { css, cva } from 'styled-system/css'
import { Flex } from 'styled-system/jsx'
import { darkmode } from './DarkMode'
import { RouteSelector } from './RouteSelector'

const compactSize = 40
const heroSize = 60

// Accent placeholder — swap to your Panda token or CSS var
const accent = 'hsl(268 90% 60%)' // <-- change me later

const glow = cva({
	base: {
		position: 'absolute',
		inset: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		pointerEvents: 'none',
	},
})

const glowDot = css({
	width: `${heroSize * 1.4}px`,
	height: `${heroSize * 1.4}px`,
	borderRadius: '999px',
	filter: 'blur(20px)',
	background:
		'radial-gradient(60% 60% at 50% 50%, var(--colors-tsc-green-default)  0%, transparent 70%)',
	opacity: 0.18,
})

export const Header = () => {
	const location = useLocation()
	const isHome = createMemo(() => location.pathname === '/')

	// Reduced motion
	const [rm, setRm] = createSignal(false)
	onMount(() => {
		const m = window.matchMedia?.('(prefers-reduced-motion: reduce)')
		setRm(!!m?.matches)
		m?.addEventListener?.('change', (e) => setRm(e.matches))
	})

	return (
		<div class={css({ position: 'relative' })}>
			<Flex
				p="0"
				alignItems="center"
				justifyContent="space-between"
			>
				{/* Left: compact logo (visible on non-root, but keeps layout space always) */}
				<Motion.img
					src={`/${darkmode() ? 'logo_dark' : 'logo'}.png`}
					alt="sniper circle logo"
					class={css({
						width: `${compactSize}px`,
						height: `${compactSize}px`,
						willChange: 'transform, opacity',
					})}
					initial={false} // 👈 disables initial={opacity:0} on re-renders
					animate={{
						opacity: isHome() ? 0 : 1,
						x: isHome() ? 20 : 0,
						scale: isHome() ? 0.95 : 1,
					}}
					transition={{
						duration: rm() ? 0 : 0.28,
						easing: 'ease-in-out',
					}}
				/>

				<Show when={isAuthenticated()}>
					<RouteSelector />
				</Show>
			</Flex>

			{/* Center overlay: hero logo on "/" */}
			<Presence exitBeforeEnter>
				<Show when={isHome()}>
					<Motion.div
						class={css({
							position: 'absolute',
							inset: 0,
							top: '12px', // your offset
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							pointerEvents: 'none',
						})}
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: rm() ? 0 : 0.2, easing: 'ease-out' }}
					>
						<Motion.img
							src={`/${darkmode() ? 'logo_dark' : 'logo'}.png`}
							alt="sniper circle logo"
							class={css({ willChange: 'transform, opacity' })}
							style={{ width: `${heroSize}px`, height: `${heroSize}px` }}
							initial={false} // prevent re-running initial on src swap
							animate={{ x: 0, scale: 1, opacity: 1 }}
							exit={{ x: -24, scale: 0.98, opacity: 0 }}
							transition={{ duration: rm() ? 0 : 0.35, easing: 'ease-in-out' }}
						/>
					</Motion.div>
				</Show>
			</Presence>
		</div>
	)
}
